import parse from "html-react-parser";
import React, { useEffect, useState } from "react";
import { useRef } from "react";
import isEqual from "react-fast-compare";
import { useDispatch, useSelector } from "react-redux";
import RichTextEditor from "./RichTextEditor";
import { setRichFieldsContent } from "../../../redux/actions/WidgetMaker/GraphConfig";
import "./RichContentContainer.scss";
import { Tooltip } from "antd";
import { SecondstoHumanReadable } from "../../utils/charts/HighCharts/graphFunction";
import { kFormatter } from "../../../redux/constants";

const RichContentContainer = ({
  data,
  section_index,
  widget_index,
  dashboard,
  PdfDownloadStatus,
  PptGeneratingStatus,
  noteExist,
  chart_type,
  id,
  style,
  metadata,
  isPreviewState,
  isdownload,
  insightsExist,
  widgetDiscriptionExist,
  widgetDiscription,
  isWidgetMakerPreview = false,
  richFieldsContent = null,
}) => {
  const dispatch = useDispatch();
  const noteRef = useRef(null);
  const widgetGraphConfig = useSelector(
    (state) => state?.GraphConfig?.widgetGraphConfig
  );
  const graphConditionConfig = useSelector(
    (state) => state?.apiCall?.graphConditions
  );
  // Check if 'data' is an array of attribute mappings or HTML content
  const initialContent =
    typeof data === "string"
      ? data
      : richFieldsContent
      ? richFieldsContent
      : "";

  const [content, setContent] = useState(initialContent);
  const [attributeFields, setAttributeFields] = useState([]);
  const [savedTokens, setSavedTokens] = useState({});

  // Dynamic values come from API response in real use
  // In the actual implementation, this would be populated from API data
  const [dynamicValues, setDynamicValues] = useState({});

  useEffect(() => {
    if (data && Array.isArray(data)) {
      const apiData = {};

      // Process data that comes in the format:
      /*
      [
        {
          "id": 0,
          "attribute": "InfluencerCategory",
          "trace_name": "Marked Influencer Category",
          "value": "Anchor"
        },
        {
          "id": 1,
          "attribute": "tagid",
          "trace_name": "Mention Count",
          "value": 1
        }
      ]
      */

      try {
        // console.log("Processing data for rich text:", data);
        data?.forEach((item) => {
          if (item?.attribute && item?.value !== undefined) {
            // Map the value to the attribute name for easier lookup
            apiData[item?.attribute] = item?.value;
            // console.log(
            //   `Mapped ${item?.attribute} (${item?.trace_name}) = ${item?.value}`
            // );

            // Also store mappings by trace_name for cases where attributes are referenced by trace name
            if (item?.trace_name) {
              apiData[item?.trace_name] = item?.value;
            }
          }
        });

        // console.log("Final mapped values:", apiData);

        // Update dynamic values with the data from props
        setDynamicValues((prevValues) => ({
          ...prevValues,
          ...apiData,
        }));
      } catch (error) {
        console.error("Error processing data for rich text:", error);
      }
    }
  }, [data]);
  // Update content when data prop changes
  useEffect(() => {
    // If data is a string, it's HTML content for the editor
    if (typeof data === "string" && data !== content) {
      setContent(data);

      // Extract tokens from the content for later use with API data
      extractTokensFromContent(data);
    }

    // If metadata.rich_fields_content is available, use that for the editor rich_fields_content
    if (richFieldsContent && richFieldsContent !== content) {
      setContent(richFieldsContent);
      extractTokensFromContent(richFieldsContent);
    } else if (
      metadata?.chart?.rich_fields_content &&
      metadata?.chart?.rich_fields_content !== content
    ) {
      setContent(metadata?.chart?.rich_fields_content);
      extractTokensFromContent(metadata?.chart?.rich_fields_content);
    } else if (
      metadata?.rich_fields_content &&
      metadata?.rich_fields_content !== content
    ) {
      setContent(metadata?.rich_fields_content);
      extractTokensFromContent(metadata?.rich_fields_content);
    }
  }, [data, metadata]);
  // Extract tokens from HTML content and save them for API data binding
  const extractTokensFromContent = (htmlContent) => {
    if (!htmlContent) return;

    // Use regex to find all tokens
    const attributeRegex = /data-attribute="([^"]+)"/g;
    const traceNameRegex = /data-trace-name="([^"]+)"/g;
    const tokens = {};
    let match;

    // First pass - get all attributes
    while ((match = attributeRegex.exec(htmlContent)) !== null) {
      const attributeName = match?.[1];
      tokens[attributeName] = {
        attribute: attributeName,
        trace_name: attributeName,
      };
    }

    // Second pass - match trace names with attributes
    let traceMatch;
    let index = 0;
    while ((traceMatch = traceNameRegex.exec(htmlContent)) !== null) {
      const traceName = traceMatch?.[1];
      // Find the attribute this trace name belongs to
      const attributeKeys = Object.keys(tokens);
      if (index < attributeKeys?.length) {
        const attributeName = attributeKeys[index];
        tokens[attributeName].trace_name = traceName;
        index++;
      }
    }

    setSavedTokens(tokens);
  };
  const getAttributeFields = (data = null, metadata = null) => {
    if (!data || !metadata) return [];
    // Extract rich fields from data or metadata
    // this list contains all attributes that can be used in x-axis, breakdown_by and y-series
    if (data && Array.isArray(data)) {
      let attributeFields = {};
      const attributeList = [];
      // Process data that comes in the format:
      /*
      [
        {
          "id": 0,
          "attribute": "InfluencerCategory",
          "trace_name": "Marked Influencer Category",
          "value": "Anchor"
        },
        {
          "id": 1,
          "attribute": "tagid",
          "trace_name": "Mention Count",
          "value": 1
        }
      ]
      */

      try {
        data?.forEach((item) => {
          if (item?.attribute && item?.value !== undefined) {
            // Try to determine if field is from x-axis, breakdown_by, or y-series
            let xAxisField = false;
            let breakdownField = false;
            if (
              metadata?.x_axis?.attribute === item?.attribute ||
              metadata?.x_axis?.trace_name === item?.trace_name ||
              item?.attribute?.includes(metadata?.x_axis?.attribute)
            ) {
              xAxisField = true;
              attributeFields = {
                ...item,
                type: metadata?.x_axis?.type || "varchar",
                source: "x-axis",
              };
            }
            let breackDownList =
              metadata?.y_axes && Array.isArray(metadata?.y_axes)
                ? metadata?.y_axes?.[0]?.attribute
                  ? metadata?.y_axes
                  : []
                : metadata?.breakdown_by &&
                  Array.isArray(metadata?.breakdown_by)
                ? metadata?.breakdown_by?.[0]?.attribute
                  ? metadata?.breakdown_by
                  : []
                : [];

            let breakdownObject = breackDownList?.find(
              (b) =>
                b?.attribute === item?.attribute ||
                b?.trace_name === item?.trace_name ||
                item?.attribute?.includes(b?.attribute)
            );
            if (breakdownObject?.attribute) {
              breakdownField = true;
              attributeFields = {
                ...item,
                type: breakdownObject?.type || "varchar",
                source: "breakdown-by",
              };
            }

            let ySeriesFromConfig = metadata?.y_series
              ? metadata?.y_series
              : Object?.entries(widgetGraphConfig?.y_axis);

            ySeriesFromConfig?.forEach((yseries, index) => {
              if (yseries?.attribute) {
                if (
                  !yseries?.hide_on_ui &&
                  (yseries?.attribute === item?.attribute ||
                    yseries?.attribute === item?.name ||
                    yseries?.trace_name === item?.trace_name ||
                    // Check if the attribute is part of the name or attribute
                    item?.name?.includes(yseries?.attribute) ||
                    item?.attribute?.includes(yseries?.attribute))
                ) {
                  attributeFields = {
                    ...item,
                    type: yseries?.type || "int",
                    source: "y-series",
                  };
                  xAxisField = false;
                  breakdownField = false;
                }
              } else if (yseries && yseries?.[1]?.attribute) {
                if (
                  !yseries?.[1]?.hide_on_ui &&
                  (yseries?.[1]?.attribute === item?.attribute ||
                    yseries?.[1]?.attribute === item?.name ||
                    yseries?.[1]?.trace_name === item?.trace_name ||
                    // Check if the attribute is part of the name or attribute
                    item?.name?.includes(yseries?.[1]?.attribute) ||
                    item?.attribute?.includes(yseries?.[1]?.attribute))
                ) {
                  attributeFields = {
                    ...item,
                    type: yseries?.[1]?.type || "int",
                    source: "y-series",
                  };
                  xAxisField = false;
                  breakdownField = false;
                }
              }
            });
            // Update source if not already set
            if (attributeFields?.attribute && !attributeFields?.source) {
              attributeFields = {
                ...item,
                source: "attribute",
              };
            }
            if (attributeFields?.attribute) {
              // Add to attribute list only if not already present
              if (
                !attributeList.some(
                  (attr) => attr?.attribute === attributeFields?.attribute
                )
              ) {
                attributeList.push(attributeFields);
              }
            }
          }
        });
      } catch (error) {
        console.error("Error processing data for attribute fields:", error);
      }
      return attributeList;
    }
  };
  // Update attribute fields from metadata based on received data
  useEffect(() => {
    const richFieldsFromConfig = getAttributeFields(data, metadata);
    setAttributeFields(richFieldsFromConfig);
  }, [
    data,
    metadata?.x_axis,
    metadata?.breakdown_by,
    metadata?.y_axes,
    metadata?.y_axis,
    metadata?.y_series,
    widgetGraphConfig?.x_axis,
    widgetGraphConfig?.breakdown_by,
    widgetGraphConfig?.y_axis,
  ]);

  // Handle proper cleanup for the ref
  useEffect(() => {
    // Proper empty useEffect with no return cleanup
  }, [noteRef, noteRef?.current?.clientHeight]);

  const handleContentChange = (newContent) => {
    setContent(newContent);

    // Extract tokens from the updated content
    extractTokensFromContent(newContent);

    // Save the rich content to the widget configuration
    if (isWidgetMakerPreview && metadata && typeof metadata === "object") {
      // Update widget metadata with new content , this metadata is same as widgetGraphConfig in Widget Maker case
      // isWidgetMakerPreview = true means we are in Widget Maker preview mode
      const updatedMetadata = {
        ...metadata,
        rich_fields_content: newContent,
      };
      dispatch(setRichFieldsContent(updatedMetadata));
    }
  };

  // Common function to format a value based on field and config
  const getFormattedValue = (field, value, graphConditionConfig) => {
    if (value === undefined || value === null) return "N/A";
    const attr = field?.attribute || field?.trace_name || field?.name;
    // Ticket time label formatting
    if (graphConditionConfig?.["ticket-time-label-attrs"]?.includes(attr)) {
      let flrValue = value?.toString()?.includes(" | ")
        ? value?.toString()?.split(" | ")
        : null;
      return flrValue && value?.toString()?.includes(" | ")
        ? SecondstoHumanReadable(flrValue?.[0], true) + " | " + flrValue?.[1]
        : SecondstoHumanReadable(value, true);
    }
    // KPI rounding
    const valueArray = isNaN(value) && value?.split(",");
    if (graphConditionConfig?.round_kpi_attrs?.includes(attr)) {
      return isNaN(value) && valueArray?.length > 1
        ? valueArray[0] + " " + valueArray[1]
        : kFormatter(value, false, true);
    }
    // Default: comma split or kFormatter
    return isNaN(value) && valueArray?.length > 1
      ? valueArray[0] + " " + valueArray[1]
      : kFormatter(value, true);
  };
  // Function to parse rich_fields_content and replace tokens with actual values
  const parseContentWithDynamicData = (htmlContent, dynamicValues) => {
    if (!htmlContent) return "";

    return htmlContent.replace(
      /\{\{attribute:([^}]+)\}\}/g,
      (match, attribute) => {
        const value = dynamicValues?.[attribute];
        const field = attributeFields?.find(
          (field) =>
            field?.attribute === attribute ||
            field?.trace_name === attribute ||
            field?.name === attribute
        );
        let formattedValue = getFormattedValue(
          field,
          value,
          graphConditionConfig
        );
        // Wrap in a span with data-raw-value and data-formatted-value
        return `<span class='dynamic-value' data-raw-value="${
          value !== undefined && value !== null
            ? String(value).replace(/"/g, "&quot;")
            : "N/A"
        }">${formattedValue !== undefined ? formattedValue : match}</span>`;
      }
    );
  };
  // Generate field cards UI based on the design image
  const renderFieldCards = () => {
    // Don't show in view/preview modes
    if (
      dashboard ||
      isPreviewState ||
      PdfDownloadStatus ||
      PptGeneratingStatus
    ) {
      return null;
    }

    if (!attributeFields || attributeFields.length === 0) {
      return (
        <div className="fields-container mb-4">
          <div className="fields-header d-flex align-items-center mb-2">
            <span className="text_regular font-weight600">
              Draggable Fields
            </span>
            <span className="text-gray font-size12 ml-2">
              (Drag to insert into the rich text)
            </span>
          </div>
          <div className="field-cards d-flex flex-wrap">
            <div className="text-gray font-size14 p-3 w-100 text-center border border-radius-4 bg-light">
              Fields not available
            </div>
          </div>
        </div>
      );
    }

    // Get field source directly from our tracking state
    const getFieldSource = (field) => {
      return field?.source || "attribute";
    }; // Function to determine colors based on field source and type - using CSS variables for theming
    const getFieldStyles = (field) => {
      const source = getFieldSource(field);
      // KpiCards-like: allow single key for bg, border, or text color, else fallback to theme
      const style = {};
      if (field?.color && field?.color != "#000000") style.bg = field.color;
      if (field?.border_color && field?.border_color != "#000000")
        style.border = field.border_color;
      if (field?.text_color && field?.text_color != "#000000")
        style.color = field.text_color;

      // First check source-based styling
      switch (source) {
        case "x-axis":
          return {
            bg: style.bg || "var(--x-axis-bg, #e6f7ff)",
            border: style.border || "var(--x-axis-border, #91d5ff)",
            color: style.color || "var(--x-axis-text, #1890ff)",
            badge: "X",
          };
        case "breakdown-by":
          return {
            bg: style.bg || "var(--breakdown-bg, #f9f0ff)",
            border: style.border || "var(--breakdown-border, #d3adf7)",
            color: style.color || "var(--breakdown-text, #722ed1)",
            badge: "B",
          };
        case "y-series":
          return {
            bg: style.bg || "var(--y-series-bg, #f6ffed)",
            border: style.border || "var(--y-series-border, #b7eb8f)",
            color: style.color || "var(--y-series-text, #52c41a)",
            badge: "Y",
          };
        default:
          // Fallback to name-based styling
          switch (field?.attribute || field?.trace_name || field?.name) {
            default:
              // Default by type
              return field?.type === "varchar"
                ? {
                    bg: style.bg || "var(--varchar-bg, #e6f7ff)",
                    border: style.border || "var(--varchar-border, #91d5ff)",
                    color: style.color || "var(--varchar-text, #1890ff)",
                  }
                : {
                    bg: style.bg || "var(--numeric-bg, #f6ffed)",
                    border: style.border || "var(--numeric-border, #b7eb8f)",
                    color: style.color || "var(--numeric-text, #52c41a)",
                  };
          }
      }
    };

    const getFieldValue = (field) => {
      // Return the value from the field or dynamic values
      // Fallback to "N/A" if not found
      if (!field) return "N/A";
      let value =
        field?.value ||
        dynamicValues?.[field?.attribute || field?.trace_name || field?.name];
      return value;
    };
    return (
      <div className="fields-container mb-4">
        <div className="fields-header d-flex align-items-center mb-2">
          <span className="text_regular font-weight600">Draggable Fields</span>
          <span className="text-gray font-size12 ml-2">
            (Drag to insert into the rich text)
          </span>
        </div>
        <div className="field-cards d-flex flex-wrap">
          {Array.isArray(attributeFields) &&
            attributeFields?.map((field, idx) => {
              const styles = getFieldStyles(field);
              const source = getFieldSource(field);
              const value = getFieldValue(field);

              return (
                <div
                  key={field?.attribute || field?.trace_name || field?.name}
                  className={`field-card field-card-${field?.type} mr-2 mb-2`}
                  style={{
                    backgroundColor: styles?.bg,
                    border: `1px solid ${styles?.border}`,
                    borderRadius: "0.4rem",
                    padding: "1rem 1.5rem",
                    minWidth: "18rem",
                    maxWidth: "24rem",
                    width: "100%",
                    flex: "1 1 24rem",
                    position: "relative",
                    cursor: "grab",
                    boxShadow: "0 0.1rem 0.2rem rgba(0,0,0,0.05)",
                    transition: "all 0.2s ease",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                  draggable
                  onDragStart={(e) => {
                    // Ensure all necessary properties are included with appropriate fallbacks
                    const fieldData = {
                      attribute: field?.attribute || field?.name,
                      name: field?.attribute || field?.name, // Include both for compatibility
                      type: field?.type || "varchar",
                      source: source,
                      trace_name:
                        field?.trace_name || field?.attribute || field?.name,
                      value: field?.value,
                    };

                    // console.log(
                    //   "Field card drag started with data:",
                    //   fieldData
                    // );
                    // Set data in multiple formats for maximum compatibility
                    e?.dataTransfer?.setData(
                      "text/plain",
                      JSON.stringify(fieldData)
                    );
                    e?.dataTransfer?.setData(
                      "application/json",
                      JSON.stringify(fieldData)
                    );
                    e?.dataTransfer?.setData("text", JSON.stringify(fieldData));

                    // Set effectAllowed to show this is a copy operation
                    e.dataTransfer.effectAllowed = "copy";

                    // Add visual feedback - add a class to highlight the field being dragged
                    e?.currentTarget?.classList?.add("dragging");
                  }}
                  onDragEnd={(e) => {
                    // Remove the class when drag ends
                    e?.currentTarget?.classList?.remove("dragging");
                  }}
                >
                  {/*  <Badge> Hinding for now */}
                  {styles?.badge && false && (
                    <div
                      className="source-badge"
                      style={{
                        position: "absolute",
                        top: "-5px",
                        right: "-5px",
                        width: "20px",
                        height: "20px",
                        borderRadius: "50%",
                        backgroundColor: styles?.color,
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "12px",
                        fontWeight: "bold",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                      }}
                    >
                      {styles?.badge}
                    </div>
                  )}
                  <Tooltip title={field?.trace_name || field?.name}>
                    <div
                      className="field-name font-weight600"
                      style={{
                        fontSize: "1.4rem",
                        color: styles?.color,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        width: "100%",
                      }}
                    >
                      {field?.trace_name || field?.name}
                    </div>
                  </Tooltip>
                  <Tooltip title={value?.toLocaleString() || "N/A"}>
                    <div
                      className="field-value"
                      style={{
                        fontSize: "1.2rem",
                        color: "rgba(0,0,0,0.65)",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        width: "100%",
                      }}
                    >
                      Value:{" "}
                      {getFormattedValue(field, value, graphConditionConfig)}
                    </div>
                  </Tooltip>

                  {/* Currently not displayed */}
                  {/* {source !== "attribute" && (
                    <div
                      className="field-source"
                      style={{
                        fontSize: "10px",
                        color: styles?.color,
                        marginTop: "4px",
                        fontWeight: "500",
                      }}
                    >
                      From: {source}
                    </div>
                  )} */}
                </div>
              );
            })}
        </div>
      </div>
    );
  };
  // Utility to remove tokens for missing attributes
  const removeMissingAttributeTokens = (htmlContent, attributeFields) => {
    if (!htmlContent) return htmlContent;
    // Build a set of valid attribute names (attribute, trace_name, name)
    const validNames = new Set();
    attributeFields?.forEach((field) => {
      if (field?.attribute) validNames.add(field.attribute);
      if (field?.trace_name) validNames.add(field.trace_name);
      if (field?.name) validNames.add(field.name);
    });
    // console.log("Valid attribute names:", validNames);
    // Replace tokens whose attribute is not in validNames
    return htmlContent.replace(/\{\{attribute:([^}]+)\}\}/g, (match, attr) => {
      // console.log("Checking attribute:", attr, match);
      return validNames.has(attr) ? match : "";
    });
  };

  const lastCleanedContentRef = useRef(null);

  // Remove tokens for deleted attributes when attributeFields change
  useEffect(() => {
    if (
      !content ||
      !(Array.isArray(attributeFields) && attributeFields?.length > 0)
    )
      return;
    const cleanedContent = removeMissingAttributeTokens(
      content,
      attributeFields
    );
    // Only update if content has truly changed and not already set
    if (
      cleanedContent !== content &&
      cleanedContent !== lastCleanedContentRef.current
    ) {
      lastCleanedContentRef.current = cleanedContent;
      setContent(cleanedContent);
    }
    // If content is already clean, update the ref
    if (cleanedContent === content) {
      lastCleanedContentRef.current = content;
    }
  }, [attributeFields]);

  // Add this helper function after your imports
  const parseContentWithLinksInNewTab = (htmlContent) => {
    return parse(htmlContent, {
      replace: (domNode) => {
        if (domNode.name === "a" && domNode.attribs) {
          domNode.attribs.target = "_blank";
          domNode.attribs.rel = "noopener noreferrer";
        }
        // Wrap dynamic-value spans in Tooltip
        if (
          domNode.name === "span" &&
          domNode.attribs &&
          domNode.attribs.class === "dynamic-value"
        ) {
          const rawValue = domNode.attribs["data-raw-value"];
          let tooltipValue = rawValue;
          if (!isNaN(rawValue) && rawValue !== "" && rawValue !== null) {
            tooltipValue = Number(rawValue).toLocaleString();
          }
          return (
            <Tooltip title={tooltipValue || "N/A"}>
              <span className="dynamic-value">
                {domNode.children &&
                  domNode.children[0] &&
                  domNode.children[0].data}
              </span>
            </Tooltip>
          );
        }
      },
    });
  };

  return (
    <>
      <div
        className={`custom_scroll1 RichText ${
          PdfDownloadStatus ? "p-10" : "p-15"
        }`}
        style={
          isWidgetMakerPreview
            ? {
                height: "calc(100% - 6.5rem)",
                overflow: "auto",
                maxHeight: "calc(100vh - 18rem)",
              }
            : isdownload && !noteExist
            ? widgetDiscriptionExist
              ? widgetDiscription?.length > 130
                ? { maxHeight: "calc(100% - 10.5rem)", overflow: "auto" }
                : { maxHeight: "calc(100% - 8.5rem)", overflow: "auto" }
              : { maxHeight: "calc(100% - 6.5rem)", overflow: "auto" }
            : style?.height
            ? { maxHeight: style?.height + "px", overflow: "auto" }
            : {}
        }
        id={id}
        ref={noteRef}
      >
        {/* Field Cards UI at the top */}
        {renderFieldCards()}
        {/* Rich Text Content Header */}
        {!dashboard &&
          !isPreviewState &&
          !PdfDownloadStatus &&
          !PptGeneratingStatus && (
            <div className="rich-text-header mb-2">
              <div className="text_regular font-weight600">
                Rich Text Content
              </div>
            </div>
          )}{" "}
        {dashboard ||
        isPreviewState ||
        PdfDownloadStatus ||
        PptGeneratingStatus ? (
          // Display mode - parse HTML with dynamic data replacements
          <div className="preview-mode">
            {parseContentWithLinksInNewTab(
              parseContentWithDynamicData(content, dynamicValues)
            )}
          </div>
        ) : (
          <RichTextEditor
            attributeFields={attributeFields}
            initialContent={typeof content === "string" ? content : ""}
            onChange={handleContentChange}
            readOnly={false}
          />
        )}
        {/* Live Preview Section */}
        {!dashboard &&
          !isPreviewState &&
          !PdfDownloadStatus &&
          !PptGeneratingStatus && (
            <div className="live-preview mt-4">
              <div className="text_regular font-weight600 mb-2">
                Live Preview
              </div>
              <div className="preview-content p-3 border border-radius-4">
                {parseContentWithLinksInNewTab(
                  parseContentWithDynamicData(content, dynamicValues)
                )}
              </div>
            </div>
          )}
      </div>
    </>
  );
};
export default React.memo(RichContentContainer, isEqual);
